import { type ChangeEvent, type KeyboardEvent, type ReactNode, type SyntheticEvent } from 'react';
import * as React from 'react';
import Downshift, {
    type GetItemPropsOptions,
    type DownshiftState,
    type StateChangeOptions,
} from 'downshift/dist/downshift.esm.js';

import { Decorators } from './components/Decorators/Decorators.tsx';
import { Dropdown } from '../../Dropdown/index.tsx';
import { DropdownOption } from './components/DropdownOption/DropdownOption.tsx';
import { ErrorMessage } from '../../ErrorMessage/index.tsx';
import { Field } from '../Field/Field.tsx';
import { FormLabel } from '../FormLabel/FormLabel.tsx';
import { InputBorderContainer } from '../InputBorderContainer/InputBorderContainer.tsx';

import { AnimatedPlaceholderContainer } from '../AnimatedPlaceholderContainer/AnimatedPlaceholderContainer.tsx';
import { TextInputContainer } from '../TextInputContainer/TextInputContainer.tsx';
import { SearchTags } from '../../SearchTags/index.tsx';
import { Badge } from '../../Badge/index.tsx';
import {
    type CommonSelectProps,
    type CommonSelectState,
    type DropdownArrowClickLifecycle,
    type NamedSelectOption,
    type MultiOnChangeArgs,
    type SelectOption,
} from './sharedTypes.ts';

import { DIRECTIONS, SIZES } from '../constants.ts';

import styles from './BaseSelect.module.css';

export type CommonMultiSelectProps = CommonSelectProps & {
    locale: string;
    onBlur?: (options: MultiOnChangeArgs) => void;
    onChange: (options: MultiOnChangeArgs) => void;
    placeholder: string;
    showSearchTags?: boolean;
    filterResults?: boolean;
    showCountBadge?: boolean;
    values: string[];
    inputRefCallback?: (input: HTMLSelectElement | HTMLInputElement) => void;
    searchTagsDataTn?: string;
};

type BaseMultiSelectProps = CommonMultiSelectProps & {
    customDropdownOption?: (arg0: {
        closeMenu: () => void;
        filteredOptions: SelectOption[];
        getItemProps: (options: GetItemPropsOptions<SelectOption>) => Record<string, $TSFixMe>; // downshift props
        inputValue: string;
        isLoading?: boolean;
        name: string;
    }) => React.ReactNode;
    maskForPrivacy?: boolean;
    searchable?: boolean;
    deselectable?: boolean;
};

type BaseMultiSelectState = CommonSelectState & {
    selections: SelectOption[];
};

const getSelectionsFromValues = (values: string[], options: SelectOption[]): SelectOption[] => {
    return options.filter(option => values.includes(option.value));
};

export class BaseMultiSelect extends React.Component<BaseMultiSelectProps, BaseMultiSelectState> {
    static defaultProps = {
        autoFocus: false,
        clearable: true,
        filterResults: true,
        errorMessageAlignment: DIRECTIONS.left,
        hasAnimatedPlaceholder: false,
        hasError: false,
        horizontalSpacing: SIZES.def,
        showSearchTags: true,
        showCountBadge: true,
        deselectable: false,
    };

    constructor(props: BaseMultiSelectProps) {
        super(props);

        this.state = {
            isFocused: !!props.autoFocus,
            inputValue: '',
            selections: getSelectionsFromValues(props.values, props.options),
        };
    }

    static getDerivedStateFromProps(
        nextProps: BaseMultiSelectProps,
        prevState: BaseMultiSelectState
    ): BaseMultiSelectState {
        return {
            ...prevState,
            selections: getSelectionsFromValues(nextProps.values, nextProps.options),
        };
    }

    componentDidMount(): void {
        // initial call to focus element if autoFocus is passed in as true

        /* istanbul ignore if */
        if (this.props.autoFocus) {
            this.focusInputNode();
        }
    }

    dropdownArrowClickLifecycle: DropdownArrowClickLifecycle = 'none';
    inputNode: { current: HTMLInputElement | null } = React.createRef();

    filterOptions = (): SelectOption[] => {
        const { options = [] } = this.props;
        const { inputValue } = this.state;

        return options.filter((option: SelectOption): boolean => {
            const compareString = option.filterString || option.label;
            return (
                !inputValue ||
                (!!compareString && compareString.toLowerCase().includes(inputValue.toLowerCase()))
            );
        });
    };

    getEventOptionParam = (
        option: SelectOption | null,
        action: 'select' | 'delete' = 'select'
    ): {
        namedSelections: MultiOnChangeArgs;
        selections: SelectOption[];
    } => {
        const { name, deselectable } = this.props;
        const { selections } = this.state;

        const label = (option && option.label) || '';
        const value = (option && option.value) || '';

        let newSelections: SelectOption[] = [...selections];

        if (option && label && value) {
            if (action === 'select') {
                const alreadyExists = newSelections.find(selection => selection.value === value);
                if (!alreadyExists) {
                    newSelections.push(option);
                } else if (deselectable) {
                    newSelections = newSelections.filter(selection => selection.value !== value);
                }
            } else if (action === 'delete') {
                newSelections = newSelections.filter(selection => selection.value !== value);
            }
        }
        const newNamedSelections = newSelections.map(selection => ({
            ...selection,
            name,
        }));

        return {
            namedSelections: newNamedSelections,
            selections: newSelections,
        };
    };

    handleBlurEvent = (isOpen: boolean, closeMenu: () => void): void => {
        const { onBlur, onInputChange } = this.props;

        if (this.dropdownArrowClickLifecycle === 'mousedown') {
            this.dropdownArrowClickLifecycle = isOpen ? 'closing' : 'none';
        }
        closeMenu();
        this.setState({
            isFocused: false,
            inputValue: '',
        });

        /* istanbul ignore else */
        if (onBlur) {
            const { namedSelections } = this.getEventOptionParam(null);
            onBlur(namedSelections);
        }

        if (onInputChange) {
            onInputChange('');
        }
    };

    handleDecoratorClick = (e: SyntheticEvent<HTMLDivElement>): void => {
        if (this.dropdownArrowClickLifecycle === 'closing') {
            e.stopPropagation();
            this.focusInputNode();
        }
        this.dropdownArrowClickLifecycle = 'none';
    };

    handleDecoratorMousedown = (): void => {
        this.dropdownArrowClickLifecycle = 'mousedown';
    };

    handleClear = (closeMenu: () => void): void => {
        const { onInputChange, onChange, searchable } = this.props;

        onChange([]);

        if (onInputChange && searchable) {
            onInputChange('');
        }
        closeMenu();

        this.setState({ inputValue: '', selections: [] });
    };

    handleChange = (option: SelectOption | null): void => {
        const { onInputChange, onChange, searchable } = this.props;

        const { namedSelections, selections } = this.getEventOptionParam(option);
        onChange(namedSelections);

        this.setState({ selections, inputValue: '' });

        if (onInputChange && searchable) {
            onInputChange('');
        }
    };

    handleTagDelete = (option: NamedSelectOption): void => {
        const { onChange } = this.props;
        const unnamedOption: SelectOption = {
            label: option.label,
            value: option.value,
        };
        const { namedSelections, selections } = this.getEventOptionParam(unnamedOption, 'delete');

        onChange(namedSelections);
        this.setState({ selections });
    };

    handleFocusEvent = (): void => {
        const { isFocused } = this.state;

        if (!isFocused) {
            this.setState({ isFocused: true });
        }
    };

    handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
        const { onInputChange, options, autoComplete, searchable } = this.props;

        const newValue = e.target ? e.target.value : '';

        this.setState({
            inputValue: newValue,
        });

        if (autoComplete) {
            const validOption = options.find(
                option => option.label.toLowerCase() === newValue.toLowerCase()
            );
            if (validOption) {
                this.handleChange(validOption);
                return;
            }
        }

        if (searchable && onInputChange) {
            onInputChange(newValue);
        }
    };

    handleKeydown = (e: KeyboardEvent<HTMLInputElement>): void => {
        switch (e.key) {
            case 'Esc':
            case 'Escape':
                this.blurInputNode();
                break;
            default:
                break;
        }
    };

    blurInputNode = (): void => {
        const inputNode = this.inputNode.current;
        if (inputNode) {
            inputNode.blur();
        }
    };

    focusInputNode = ({
        menuToggle,
    }: {
        menuToggle?: () => void;
    } = {}): void => {
        if (this.inputNode.current && !this.props.disabled) {
            this.inputNode.current.focus();
            if (menuToggle) {
                menuToggle();
            }
        }
    };

    itemToString = (item: SelectOption | null): string => {
        if (item && item.label) {
            return item.label;
        }
        return '';
    };

    shouldShowAnimatedPlaceholder = (): boolean => {
        const { hasAnimatedPlaceholder, placeholder, searchable } = this.props;
        const { isFocused, inputValue } = this.state;
        return (
            !!searchable &&
            !!hasAnimatedPlaceholder &&
            !!placeholder &&
            (!!inputValue || !!isFocused)
        );
    };

    render(): React.ReactNode {
        const {
            autoComplete,
            clearable,
            customDropdownOption,
            dataTn,
            disabled,
            dropdownDirection = 'below',
            errorMessage,
            errorMessageAlignment,
            horizontalSpacing,
            isLoading,
            label,
            locale,
            maskForPrivacy,
            name,
            placeholder,
            searchable,
            showSearchTags,
            showCountBadge,
            size,
            filterResults,
            options = [],
            inputRefCallback,
            searchTagsDataTn,
            id: propId,
        } = this.props;

        const { isFocused, inputValue, selections } = this.state;

        const hasError = this.props.hasError || !!errorMessage;
        const errorDataTn = `${dataTn}-error`;

        const showBadge: boolean = !showSearchTags && !!showCountBadge && selections.length > 0;
        const selectionCount: string = new Intl.NumberFormat(locale, {
            maximumFractionDigits: 0,
        }).format(selections.length);

        const inputAutoComplete = autoComplete || 'off';
        const filteredOptions = filterResults ? this.filterOptions() : options;

        const dropdown = (
            direction: 'above' | 'below',
            isOpen: boolean,
            closeMenu: () => void,
            customOption: ReactNode,
            getItemProps: (options: GetItemPropsOptions<SelectOption>) => void,
            highlightedIndex: number | null,
            selectedItem: SelectOption[]
        ): ReactNode =>
            dropdownDirection === direction && (
                <Dropdown
                    dataTn={`${dataTn}-dropdown`}
                    hideTopBorder
                    isOpen={isOpen && (!searchable || filteredOptions.length > 0 || !!customOption)}
                    matchContainerWidth
                    reverseDirection={direction === 'above'}
                    detachedMode={this.props.dropdownDetachedMode}
                >
                    <div className={styles.dropdownOptionsWrapper} style={{ maxHeight: 200 }}>
                        <div className={styles.filteredOptionsWrapper}>
                            {filteredOptions.map((item, index) => (
                                <DropdownOption
                                    // @ts-expect-error can't spread void, wrong type for getItemProps?
                                    {...getItemProps({
                                        item,
                                        onClick: () =>
                                            this.focusInputNode({
                                                menuToggle: closeMenu,
                                            }),
                                    })}
                                    key={`${item.value}-${index}`}
                                    dataTn={`${dataTn}-option-${index}`}
                                    isHighlighted={highlightedIndex === index}
                                    isSelected={selectedItem.some(
                                        (singleItem: SelectOption) =>
                                            singleItem.value === item.value
                                    )}
                                >
                                    {item.customDropdownDisplay || item.label}
                                </DropdownOption>
                            ))}
                        </div>
                        {customOption}
                    </div>
                </Dropdown>
            );

        const stateReducer = (
            state: DownshiftState<SelectOption>,
            changes: StateChangeOptions<SelectOption>
        ): Partial<StateChangeOptions<SelectOption>> => {
            switch (changes.type) {
                case Downshift.stateChangeTypes.keyDownEnter:
                case Downshift.stateChangeTypes.clickItem:
                    return {
                        ...changes,
                        highlightedIndex: state.highlightedIndex,
                        isOpen: true,
                        inputValue: '',
                    };
                default:
                    return changes;
            }
        };

        return (
            // downshift accepts a single generic. We need both SelectOption and SelectOption[] (for selectedItem={selections})
            <Downshift<$TSFixMe>
                itemToString={this.itemToString}
                onChange={this.handleChange}
                inputValue={inputValue}
                selectedItem={selections}
                stateReducer={stateReducer}
            >
                {({
                    closeMenu,
                    getInputProps,
                    getItemProps,
                    highlightedIndex,
                    isOpen,
                    openMenu,
                    selectedItem,
                }) => {
                    const spacing = horizontalSpacing === SIZES.small ? SIZES.small : SIZES.medium;

                    const customOption =
                        customDropdownOption &&
                        customDropdownOption({
                            getItemProps,
                            closeMenu: () => this.focusInputNode({ menuToggle: closeMenu }),
                            filteredOptions,
                            inputValue,
                            isLoading,
                            name,
                        });

                    const animatedOptionalProps: { onClick?: () => void } = {};
                    if (!disabled) {
                        animatedOptionalProps.onClick = openMenu;
                    }
                    let arrowDirection = isOpen ? DIRECTIONS.up : DIRECTIONS.down;
                    if (dropdownDirection === 'above') {
                        arrowDirection = isOpen ? DIRECTIONS.down : DIRECTIONS.up;
                    }

                    const inputId = propId || getInputProps().id;

                    // container base div is used by downshift. Do not remove.
                    return (
                        <div>
                            <Field>
                                <FormLabel htmlFor={inputId}>{label}</FormLabel>
                                {dropdown(
                                    'above',
                                    isOpen,
                                    closeMenu,
                                    customOption,
                                    getItemProps,
                                    highlightedIndex,
                                    selectedItem
                                )}
                                <InputBorderContainer
                                    disabled={disabled}
                                    hasError={hasError}
                                    isFocused={isFocused}
                                    onClick={() =>
                                        this.focusInputNode({
                                            menuToggle: openMenu,
                                        })
                                    }
                                    size={size}
                                    maskForPrivacy={maskForPrivacy}
                                >
                                    <AnimatedPlaceholderContainer
                                        marginLeft={spacing}
                                        marginRight={spacing}
                                        placeholder={placeholder}
                                        showAnimatedPlaceholder={this.shouldShowAnimatedPlaceholder()}
                                        {...animatedOptionalProps}
                                    >
                                        {({ placeholderDidRender }) => (
                                            <TextInputContainer
                                                paddingTop={
                                                    placeholderDidRender
                                                        ? SIZES.animatedPlaceholder
                                                        : SIZES.none
                                                }
                                            >
                                                <input
                                                    {...getInputProps({
                                                        id: inputId,
                                                        placeholder: placeholderDidRender
                                                            ? ''
                                                            : placeholder,
                                                        autoComplete: inputAutoComplete,
                                                        autoFocus: isFocused,
                                                        disabled,
                                                        name,
                                                        onBlur: () =>
                                                            this.handleBlurEvent(isOpen, closeMenu),
                                                        onChange: this.handleInputChange,
                                                        onFocus: this.handleFocusEvent,
                                                        onKeyDown: this.handleKeydown,
                                                        readOnly: !searchable,
                                                        value: inputValue,
                                                        type: 'text',
                                                    })}
                                                    data-tn={`${dataTn}-input`}
                                                    ref={input => {
                                                        this.inputNode.current = input;
                                                        if (input && inputRefCallback) {
                                                            inputRefCallback(input);
                                                        }
                                                    }}
                                                />
                                            </TextInputContainer>
                                        )}
                                    </AnimatedPlaceholderContainer>
                                    <Decorators
                                        arrowDirection={arrowDirection}
                                        arrowPaddingRight={spacing}
                                        clearable={!!clearable}
                                        dataTn={dataTn}
                                        disabled={!!disabled}
                                        isLoading={!!isLoading}
                                        onClick={this.handleDecoratorClick}
                                        onMouseDown={this.handleDecoratorMousedown}
                                        onClear={() => this.handleClear(closeMenu)}
                                    >
                                        {showBadge && (
                                            <Badge type="neutral">{selectionCount}</Badge>
                                        )}
                                    </Decorators>
                                </InputBorderContainer>
                                {dropdown(
                                    'below',
                                    isOpen,
                                    closeMenu,
                                    customOption,
                                    getItemProps,
                                    highlightedIndex,
                                    selectedItem
                                )}
                                <ErrorMessage
                                    message={errorMessage}
                                    dataTn={errorDataTn}
                                    alignRight={errorMessageAlignment === DIRECTIONS.right}
                                />
                                {showSearchTags && (
                                    <SearchTags
                                        disabled={!!disabled}
                                        onDelete={this.handleTagDelete}
                                        name={name}
                                        tags={selectedItem}
                                        tagSpacing="xSmall"
                                        dataTn={searchTagsDataTn}
                                    />
                                )}
                            </Field>
                        </div>
                    );
                }}
            </Downshift>
        );
    }
}
